// Static content arrays for LandingPage. Edit copy here without touching component logic.
// Icons are imported alongside data so components can use them directly.

import {
  Clock, Sparkle, ChartLineUp, ShieldCheck, Truck,
  EnvelopeSimple, ChatCircleDots, FlowArrow, Files, ReceiptX, Phone,
  MapTrifold, MicrophoneStage,
  UserFocus, CheckCircle, GraduationCap, Circuitry,
} from "@phosphor-icons/react";

export const BENEFITS = [
  { icon: Clock, label: "Save valuable staff time" },
  { icon: Sparkle, label: "Reduce manual work and errors" },
  { icon: ChartLineUp, label: "Improve team productivity" },
  { icon: ShieldCheck, label: "Secure and reliable workflows" },
  { icon: Truck, label: "Built specifically for trucking" },
];

export const PROBLEMS = [
  { icon: EnvelopeSimple, t: "Reading and organizing broker emails" },
  { icon: ChatCircleDots, t: "Repeating customer status updates" },
  { icon: FlowArrow, t: "Entering information into multiple systems" },
  { icon: Files, t: "Processing rate confirmations and documents" },
  { icon: ReceiptX, t: "Preparing invoices and follow-ups" },
  { icon: Phone, t: "Managing repetitive calls and messages" },
];

export const SOLUTIONS = [
  {
    icon: MapTrifold, title: "AI Dispatch Support",
    body: "Organize load information, prioritize tasks, and help dispatchers respond faster.",
    to: "/for/dispatch",
  },
  {
    icon: EnvelopeSimple, title: "AI Email Automation",
    body: "Read, categorize, summarize, and route routine operational emails.",
    to: "/services#email",
  },
  {
    icon: ChatCircleDots, title: "AI Customer Updates",
    body: "Deliver faster, more consistent shipment and service communications.",
    to: "/services#customer",
  },
  {
    icon: Files, title: "AI Document Processing",
    body: "Extract and organize information from rate confirmations, BOLs, PODs, and invoices.",
    to: "/services#documents",
  },
  {
    icon: MicrophoneStage, title: "AI Voice Assistant",
    body: "Handle routine incoming questions and route important calls to the right person.",
    to: "/services#voice",
  },
  {
    icon: FlowArrow, title: "Custom Workflow Automation",
    body: "Connect repetitive steps across your existing operating process.",
    to: "/services#workflow",
  },
];

export const STEPS = [
  { n: "01", title: "Assess",    body: "We review your current workflow and identify time-consuming manual tasks." },
  { n: "02", title: "Implement", body: "We configure practical automations around your company's processes and tools." },
  { n: "03", title: "Improve",   body: "We measure results, train your team, and improve the workflow over time." },
];

export const TRUST_POINTS = [
  { icon: UserFocus, t: "Human review and approval options" },
  { icon: ShieldCheck, t: "Role-based access where supported" },
  { icon: ShieldCheck, t: "Secure data handling" },
  { icon: CheckCircle, t: "Workflow testing before launch" },
  { icon: GraduationCap, t: "Training and implementation support" },
  { icon: Circuitry, t: "Integration planning with existing systems" },
];

export const FAQS = [
  {
    q: "What does FreightPilotAI automate?",
    a: "We help trucking companies automate routine office and communication work — broker email triage, customer status updates, document extraction (rate cons, BOLs, PODs, invoices), invoicing and follow-up, dispatch coordination, and custom workflow steps across the tools you already use. The scope depends on your team and processes, which is what the free assessment is designed to uncover.",
  },
  {
    q: "Will it replace our employees?",
    a: "No. FreightPilotAI is designed to remove repetitive work from your team's day so they can focus on higher-value work — customer relationships, load negotiation, driver support, and growth. Humans stay in control of important decisions; automation handles the routine steps around them.",
  },
  {
    q: "Can it work with our existing systems?",
    a: "In most cases, yes. We plan integrations around the tools you already use — TMS, email, phone systems, spreadsheets, load boards, and messaging platforms. During the assessment we identify which connections are feasible and which are best solved with light-touch automation instead.",
  },
  {
    q: "How long does implementation take?",
    a: "It varies by scope. Straightforward workflows can be running within a few weeks; broader implementations across multiple processes take longer. We prioritize the highest-impact automations first so your team sees value early rather than waiting for a big-bang launch.",
  },
  {
    q: "Is our business information secure?",
    a: "We treat your operational data as sensitive by default: encrypted in transit, access-controlled, and only used to run the automations you approve. We can walk through specific security and data-handling questions during the assessment.",
  },
  {
    q: "What size trucking company is the best fit?",
    a: "FreightPilotAI is built for trucking operations of roughly 5 to 500 trucks — fleet owners, dispatch teams, freight brokers, and logistics companies with enough repetitive office work that automation makes a meaningful difference. Very small owner-operators and very large enterprise carriers may be a partial fit; the assessment tells you honestly.",
  },
  {
    q: "What happens during the free assessment?",
    a: "A short conversation about how your team currently works — where time is spent, which tasks feel repetitive, and which tools you rely on. We produce a written summary of the biggest opportunities, likely time savings ranges, and a suggested first automation to start with. No obligation to move forward.",
  },
  {
    q: "How is pricing determined?",
    a: "Pricing depends on the automations you choose and the size of your operation. Some customers start with a single workflow at a fixed monthly rate; others take on a broader implementation. We share transparent pricing after the assessment so it's tied to real scope rather than an off-the-shelf list.",
  },
];

export const NAV_LINKS = [
  { label: "Free Tools", to: "/tools", external: true },
  { label: "DOT Check", to: "/broker-check", external: true },
  { label: "Solutions", to: "#solutions" },
  { label: "How It Works", to: "#how-it-works" },
  { label: "Pricing", to: "/pricing", external: true },
  { label: "Resources", to: "/blog", external: true },
  { label: "About", to: "#about" },
  { label: "Contact", to: "#assessment" },
];
